import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mapAuthError, type MappedApiError } from '@mybusinessmate/api-client';
import { authClient } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useToast } from '../ui/toast';

const accountsKey = ['auth', 'accounts'] as const;
const sessionsKey = ['auth', 'sessions'] as const;

/**
 * The signed-in person's sign-in methods and devices, with the actions on them: connect or disconnect a social
 * account, sign a device out, sign out everywhere else. Shared by the client and staff Security pages.
 */
export function useAccountSecurity() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: current } = useSession();
  const [problem, setProblem] = useState<string>();

  const accounts = useQuery({
    queryKey: accountsKey,
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts();
      if (error) throw mapAuthError(error);
      return data ?? [];
    },
  });
  const sessions = useQuery({
    queryKey: sessionsKey,
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      if (error) throw mapAuthError(error);
      return data ?? [];
    },
  });

  const link = useMutation({
    mutationFn: async (provider: 'google' | 'facebook') => {
      const { error } = await authClient.linkSocial({
        provider,
        callbackURL: `${window.location.origin}/settings/security`,
      });
      if (error) throw mapAuthError(error);
    },
    onError: (e: MappedApiError) =>
      setProblem(e.code === 'PROVIDER_NOT_FOUND' ? 'That sign-in method isn’t available right now.' : e.message),
  });

  // Better Auth unlinks by the account row's own id, and only for a recently signed-in session.
  const unlink = useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await authClient.unlinkAccount({ accountId });
      if (error) throw mapAuthError(error);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: accountsKey }),
    onError: (e: MappedApiError) =>
      setProblem(
        e.code === 'FAILED_TO_UNLINK_LAST_ACCOUNT'
          ? 'You can’t disconnect your only way to sign in.'
          : e.code === 'SESSION_NOT_FRESH'
            ? 'For your security, sign in again before changing your sign-in methods.'
            : e.message,
      ),
  });

  const revoke = useMutation({
    mutationFn: async (token: string) => {
      const { error } = await authClient.revokeSession({ token });
      if (error) throw mapAuthError(error);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionsKey }),
    onError: (e: MappedApiError) => setProblem(e.message),
  });

  const revokeOthers = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.revokeOtherSessions();
      if (error) throw mapAuthError(error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionsKey });
      toast({ title: 'Signed out of your other devices' });
    },
    onError: (e: MappedApiError) => setProblem(e.message),
  });

  const otherDevices = (sessions.data ?? []).filter((s) => s.token !== current?.session.token);

  return {
    current,
    accounts,
    sessions,
    otherDevices,
    credential: accounts.data?.find((a) => a.providerId === 'credential'),
    google: accounts.data?.find((a) => a.providerId === 'google'),
    problem,
    setProblem,
    link,
    unlink,
    revoke,
    revokeOthers,
  };
}
